/**
 * useFormLifecycle
 *
 * Generalized form submission lifecycle hook used by every CRA form
 * that does not warrant the full React Hook Form machinery (per the
 * decision matrix in `docs/architecture/forms-standard.md`):
 *
 *   - Validates against a Zod schema each render so `canSubmit` is
 *     always in sync with the latest values.
 *   - Tracks `isSubmitting` and a single `error` string.
 *   - Reports `canSubmit` / `isSubmitting` to a parent through optional
 *     callbacks (used by modals that render their submit button in a
 *     footer slot via `FormActions`).
 *   - Exposes `validateOrError()` so submit handlers can short-circuit
 *     with a localized message when validation fails just before
 *     hitting the network.
 *
 * Successor to `usePostFormLifecycle`. The previous hook is kept as a
 * deprecation shim that re-exports from this file so existing callers
 * compile unchanged for one release; new code must import the new name.
 */
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useState } from "react";
import type { z } from "zod";

export interface UseFormLifecycleParams<TValues> {
  /**
   * Returns the latest form values to validate. Called on every render
   * so it should be cheap (a closure reading state, not a fetch).
   */
  getValues: () => TValues;
  /** Zod schema used to compute `canSubmit` and `validateOrError`. */
  schema: z.ZodType<TValues>;
  /**
   * Extra readiness gate, ANDed with schema validity. Use for
   * resolved-issue / not-uploading / required-context conditions that
   * are not part of the field schema itself.
   */
  isReady: boolean;
  /** Notified when `canSubmit` flips. Useful for parent footer buttons. */
  onCanSubmitChange?: (can: boolean) => void;
  /** Notified when `isSubmitting` flips. Useful for parent footer spinners. */
  onSubmittingChange?: (submitting: boolean) => void;
}

export interface UseFormLifecycleResult {
  /** Latest user-visible error string. Empty string when no error. */
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  isSubmitting: boolean;
  setIsSubmitting: Dispatch<SetStateAction<boolean>>;
  /** True iff the schema validates AND `isReady` AND not already submitting. */
  canSubmit: boolean;
  /** First schema error message (or null when valid). */
  validateOrError: () => string | null;
  /** Convenience: clear error + set submitting. */
  beginSubmit: () => void;
  /** Convenience: clear submitting. */
  endSubmit: () => void;
}

/**
 * Hook implementation. See file-level docstring for the contract.
 *
 * The hook intentionally does NOT own the network call -- consumers
 * still write their own try/catch and wire `setError(normalizeApiError(...))`
 * for transport failures. This keeps the hook small and lets each
 * form decide between toasts (via `notify`) vs inline banner UX.
 */
export function useFormLifecycle<TValues>(
  params: UseFormLifecycleParams<TValues>
): UseFormLifecycleResult {
  const { getValues, schema, isReady, onCanSubmitChange, onSubmittingChange } = params;
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const parsed = schema.safeParse(getValues());
  const canSubmit = parsed.success && isReady && !isSubmitting;

  useEffect(() => {
    onCanSubmitChange?.(canSubmit);
  }, [canSubmit, onCanSubmitChange]);

  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const validateOrError = useCallback((): string | null => {
    const result = schema.safeParse(getValues());
    if (result.success) {
      return null;
    }
    return result.error.issues[0]?.message ?? "Validation failed.";
  }, [getValues, schema]);

  const beginSubmit = useCallback(() => {
    setIsSubmitting(true);
    setError("");
  }, []);

  const endSubmit = useCallback(() => {
    setIsSubmitting(false);
  }, []);

  return {
    error,
    setError,
    isSubmitting,
    setIsSubmitting,
    canSubmit,
    validateOrError,
    beginSubmit,
    endSubmit,
  };
}
